import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Video, ChefHat, ImagePlus, Camera, 
  Sparkles, Flame
} from 'lucide-react';

export function CreateMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const menuItems = [
    { 
      icon: Video, 
      labelKey: 'create.video', 
      descKey: 'create.videoDesc',
      path: '/create',
      color: 'from-[#1E4ED8] to-[#3B82F6]',
      badgeKey: 'create.popular'
    },
    { 
      icon: ChefHat, 
      labelKey: 'create.recipe', 
      descKey: 'create.recipeDesc',
      path: '/create?type=recipe',
      color: 'from-[#D9381E] to-[#E07A5F]',
      badgeKey: null
    },
    { 
      icon: ImagePlus, 
      labelKey: 'create.textPost', 
      descKey: 'create.textPostDesc',
      path: '/create?type=text',
      color: 'from-[#4F6F52] to-[#6B8E6B]',
      badgeKey: null
    },
    { 
      icon: Camera, 
      labelKey: 'create.poll', 
      descKey: 'create.pollDesc',
      path: '/create?type=poll',
      color: 'from-[#E07A5F] to-[#F4A261]',
      badgeKey: null
    },
  ];

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-md md:w-full"
          >
            <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-fraunces text-xl font-bold">{t('create.title')}</h2>
                      <p className="text-white/80 text-sm">{t('create.whatToShare')}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                    data-testid="close-create-menu"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Menu Items */}
              <div className="p-4 space-y-3">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.labelKey}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleNavigate(item.path)}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-[#F8F5F2] hover:bg-[#1E4ED8]/5 transition-all group border border-transparent hover:border-[#1E4ED8]/20"
                    data-testid={`create-${t(item.labelKey).toLowerCase()}-btn`}
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 group-hover:text-[#1E4ED8] transition-colors">
                          {t(item.labelKey)}
                        </span>
                        {item.badgeKey && (
                          <span className="text-xs bg-[#D9381E] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            {t(item.badgeKey)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{t(item.descKey)}</p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-gray-400 group-hover:text-[#1E4ED8] group-hover:bg-[#1E4ED8]/10 transition-all">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Footer hint */}
              <div className="px-6 pb-6 pt-2">
                <p className="text-center text-sm text-gray-400">
                  {t('create.earnBarka')}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default CreateMenu;
